// Tech Support Co-Pilot — signature editor modal.
//
// John's sign-off, stored per-user (reuses the orders-portal EmailSignature
// store) and auto-appended to every reply he sends from the co-pilot. Pre-filled
// with his real ChemMasters sign-off; editable here. Shows a live preview + the
// HTML source (mirrors the portal Settings signature editor's source mode).

import { useEffect, useState } from 'react';
import { resetSignature, updateSignature } from '../../services/techSupportCopilot';
import styles from './TechSupportSignatureModal.module.css';

interface Props {
  isOpen: boolean;
  /** Current signature HTML (the page owns it; this seeds the editor on open). */
  initialHtml: string;
  onClose: () => void;
  /** Called with the saved HTML so the page (and the draft-card preview) update. */
  onSaved: (html: string) => void;
}

export default function TechSupportSignatureModal({
  isOpen,
  initialHtml,
  onClose,
  onSaved,
}: Props) {
  const [html, setHtml] = useState(initialHtml);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reseed the editor each time it opens (or the stored signature changes).
  useEffect(() => {
    if (isOpen) {
      setHtml(initialHtml);
      setError(null);
    }
  }, [isOpen, initialHtml]);

  if (!isOpen) return null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateSignature(html);
      onSaved(res.signature_html);
      onClose();
    } catch {
      setError('Could not save your signature — please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Server re-renders the corporate format from the user record and saves it;
  // the editor and the page's stored copy both pick up the result.
  const reset = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await resetSignature();
      setHtml(res.signature_html);
      onSaved(res.signature_html);
    } catch {
      setError('Could not reset your signature — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Edit your email signature"
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <span className={styles.title}>Your email signature</span>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p className={styles.note}>
          This is added automatically to the bottom of every reply you send — the
          co-pilot won&apos;t repeat it in its drafts.
        </p>
        <p className={styles.note}>
          Edit it however you like: deleting the title or phone lines is fine for
          replies to homeowners. &ldquo;Reset to corporate format&rdquo; rebuilds the
          full block (title and phone included) from your user record. This one
          signature is also used when you send from the orders portal.
        </p>

        <div className={styles.fieldLabel}>Preview</div>
        <div
          className={styles.preview}
          // John's own signature HTML (self-authored) — mirrors the portal editor.
          dangerouslySetInnerHTML={{ __html: html }}
        />

        <div className={styles.fieldLabel}>Signature (HTML)</div>
        <textarea
          className={styles.editor}
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          rows={8}
          spellCheck={false}
          aria-label="Signature HTML"
        />

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            onClick={() => void reset()}
            disabled={saving}
            title="Replace the signature with the corporate-mandate format"
          >
            Reset to corporate format
          </button>
          <button type="button" className={styles.cancel} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.save}
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save signature'}
          </button>
        </div>
      </div>
    </div>
  );
}
